import type { ReactNode } from 'react'
import { Badge, Skeleton } from '@teach/ui'
import type { Lang } from '@/types/lang'
import type { LanguageState } from '@/features/ai/studioStartTypes'
import { parseStartError } from '@/features/ai/lib/parseStartError'
import { translationPublishFailureText } from '@/features/ai/lib/translationPublishFailureText'
import { LANG_INFO } from '@/features/editor/lib/courseLanguages'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { VisibilitySwitch } from './VisibilitySwitch'
import { useTranslationPublishToggle } from './useTranslationPublishToggle'
import { LangStatusChip } from '../EditorHeader/LangStatusChip'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  courseId: number
  lang: Lang
  /** Строка `languages[]` сметы (§6.1); `null` — смета не доехала или старый
   *  бэк. Рисует чип статуса рядом с бейджем языка — тот же источник, что у
   *  меню языка шапки, своей копии правил тут нет. */
  state: LanguageState | null
  isPublished: boolean
  /** Перевод залит, а строки языка нет — считает `unpublishedFilledLang`,
   *  зеркало серверного правила. Своей копии условия здесь нет намеренно:
   *  студия и MCP обязаны говорить автору одно и то же. */
  isLangUndeclared?: boolean
  /** Сам курс ещё не опубликован. Тогда включённый язык не «показывается в
   *  каталоге» — курса там нет ни на одном языке, — а объявлен на будущее.
   *  Подпись обязана различать это: врать в другую сторону не лучше. */
  isCourseDraft?: boolean
  /** Бейдж языка уже нарисован карточкой выше — не дублируем. */
  hideBadge?: boolean
  /** Курс перечитывается: тумблер ждёт, пока сервер и экран сойдутся. */
  locked?: boolean
  /** Сервер пересчитывает языки — чип и тумблер скелетонами. */
  loading?: boolean
  /** Части строки раскладывает владелец (карточка языка). */
  layout?: (parts: { status: ReactNode; control: ReactNode; notes: ReactNode }) => ReactNode
}

/**
 * Строка языка перевода. Бейдж — как в рефе; удаление — из меню «⋯» с
 * подтверждением (§6.9), а не крестиком на самом бейдже: диалог
 * (`DeleteLanguageDialog`) живёт рядом, во вкладке «Язык» (`LanguagesSection`).
 *
 * Переключатель НИКОГДА не гасится по неполноте перевода: ручка полноту не
 * проверяет, и на проде уже есть курс, опубликованный в en без единого
 * переведённого поля. Неполнота объясняется баннером, а не запретом.
 *
 * Объяснение «язык не объявлен» с неполнотой не связано: число мест к
 * переводу может не дойти до нуля никогда (слово, одинаковое в обоих языках,
 * попадает в план каждого прогона), и завязывать объяснение на него значит
 * не показать его ровно там, где нужнее всего.
 *
 * Отказ показываем ЗДЕСЬ, а не тостом: два `422` этой ручки
 * (`translation_storage_limit`, `unsupported_language`) уже разобраны словарём
 * перевода — иначе автор получал бы сырой английский `message` (ручка убрана
 * из тостов в `errorToastMiddleware`, чтобы текст не задваивался).
 *
 * Текст — `translationPublishFailureText`, а НЕ общий `failureText` словаря
 * стартов: у того фолбэк на неразобранный код — «Не удалось запустить
 * прогон», но эта ручка прогон не запускает, и такой заголовок соврал бы про
 * суть отказа (сеть/`500`/незнакомый код).
 */
export const TranslationLangRow = ({
  courseId,
  lang,
  state,
  isPublished,
  isLangUndeclared,
  isCourseDraft,
  hideBadge,
  locked = false,
  loading,
  layout,
}: Props) => {
  const m = useMessages(messages)
  const publish = useTranslationPublishToggle(courseId, lang, isPublished)
  // Показ языка в каталоге — часть заявки на проверку, пока она идёт.
  const isFrozen = useIsCourseFrozen(courseId)
  const info = LANG_INFO[lang]
  const switchId = `translation-published-${lang}`
  const status = loading ? (
    <Skeleton className="h-3.5 w-24 rounded-full" />
  ) : (
    <LangStatusChip state={state} />
  )
  const control = (
    <VisibilitySwitch
      id={switchId}
      label={m.visibility}
      hint={publish.checked ? m.visibilityOn : m.visibilityOff}
      checked={publish.checked}
      large
      pending={publish.isSaving || locked || loading}
      disabled={isFrozen || publish.isSaving || locked || loading}
      onCheckedChange={(next) => {
        if (refusedByReview(isFrozen)) return
        publish.toggle(next)
      }}
    />
  )
  const notes = (
    <>
      {publish.error ? (
        <p className="text-xs text-destructive">
          {translationPublishFailureText(parseStartError(publish.error))}
        </p>
      ) : null}
      {isLangUndeclared && !loading ? (
        // У черновика обещать студентов нельзя: включённый переключатель
        // ОБЪЯВИТ язык, но курса нет в каталоге ни на одном языке, пока он
        // не опубликован. Правило намеренно не смотрит на публикацию курса
        // (MCP заливает переводы именно в черновики), поэтому главный
        // адресат подсказки — как раз тот, кому эта фраза соврала бы.
        <p className="text-xs text-warning">
          {isCourseDraft ? m.undeclaredDraft : m.undeclaredLive}
        </p>
      ) : null}
    </>
  )
  if (layout) return <>{layout({ status, control, notes })}</>
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {hideBadge ? null : (
          <Badge variant="secondary" className="flex items-center gap-1">
            <span>{info.flag}</span>
            <span>{info.name}</span>
          </Badge>
        )}
        {status}
        <div className="ml-auto flex items-center gap-2">{control}</div>
      </div>
      {notes}
    </div>
  )
}
